import { Trophy, Crown, Flag, Globe2, Target, Sparkles, Flame, Skull, Award, ScrollText, Users, Clock, Lock, Ticket } from "lucide-react";

const FESTIVAL_ROWS = [
  { name: "Roskilde Festival", color: "#FF4500", head: 4, intl: 16, dansk: 6, extra: "Orange Scene-åbner" },
  { name: "NorthSide", color: "#6200EA", head: 3, intl: 5, dansk: 5, extra: null },
  { name: "Syd for Solen", color: "#FFD600", head: 3, intl: 10, dansk: 4, extra: null },
  { name: "Tinderbox", color: "#00BFFF", head: 3, intl: 8, dansk: 6, extra: null },
  { name: "Smukfest", color: "#00C853", head: 4, intl: 7, dansk: 7, extra: null },
];

const POINTS = [
  { label: "Headliner", value: 12, hint: "Rammer du en headliner, som festivalen officielt annoncerer som headliner", tone: "#F59E0B" },
  { label: "International artist", value: 4, hint: "Rammer du en international artist på festivalen", tone: "#60A5FA" },
  { label: "Dansk artist", value: 1, hint: "Rammer du en dansk artist på festivalen", tone: "#F87171" },
  { label: "Orange Scene-åbner", value: 10, hint: "Rammer du artisten der åbner Orange Scene på Roskilde", tone: "#FF4500" },
];

const BADGES = [
  { id: "orakel", name: "Oraklet", icon: "🔮", color: "#A78BFA", desc: "Ramte din første headliner." },
  { id: "headliner_hunter", name: "Headliner Hunter", icon: "👑", color: "#F59E0B", desc: "Ramte 3 headlinere på tværs af festivaler." },
  { id: "dead_eye", name: "Dead Eye", icon: "🎯", color: "#EF4444", desc: "Ramte alle headlinere på én festival." },
  { id: "globetrotter", name: "Globetrotter", icon: "🌍", color: "#60A5FA", desc: "Ramte 10 internationale artister." },
  { id: "danmarkskender", name: "Danmarkskender", icon: "🇩🇰", color: "#F87171", desc: "Ramte 10 danske artister." },
  { id: "wildcard", name: "Wild Card", icon: "🧨", color: "#FB923C", desc: "Ramte en artist som færre end 5 % forudsagde." },
  { id: "on_fire", name: "On Fire", icon: "🔥", color: "#F97316", desc: "Fem hits i træk efter annonceringsrækkefølgen." },
  { id: "festival_boss", name: "Festival Boss", icon: "👑", color: "#FBBF24", desc: "Vandt en festival-liga." },
  { id: "the_manager", name: "The Manager", icon: "🏆", color: "#FCD34D", desc: "Vandt den samlede Festival Manager-liga." },
  { id: "bold_but_wrong", name: "Bold But Wrong", icon: "💀", color: "#71717A", desc: "En modig gæt uden held – under 5 % og artisten spillede ikke." },
];

function SectionKicker({ n, label, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="h-8 w-8 rounded-full border border-white/15 flex items-center justify-center text-zinc-400">
        <Icon size={14} />
      </div>
      <div className="label-eyebrow tracking-[0.2em]">
        <span className="text-zinc-500">{String(n).padStart(2, "0")}</span>
        <span className="mx-2 text-zinc-700">/</span>
        <span>{label}</span>
      </div>
    </div>
  );
}

export default function Rules() {
  const totalSlots = 92; // 91 artist-picks + 1 Orange Scene opener
  const maxPoints = 426; // total across all categories per user's spec

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="rules-page">
      {/* Hero */}
      <div className="pt-4">
        <div className="label-eyebrow flex items-center gap-2">
          <ScrollText size={12} /> Konkurrencen
        </div>
        <h1 className="hero-title text-5xl sm:text-7xl lg:text-8xl mt-3">Regler</h1>
        <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-zinc-500">
          <div className="flex items-center gap-2"><Ticket size={14} /> 5 festivaler</div>
          <div className="flex items-center gap-2"><Users size={14} /> {totalSlots} artistgæt</div>
          <div className="flex items-center gap-2"><Trophy size={14} /> {maxPoints} maks point</div>
        </div>
      </div>

      {/* 01 – Sådan spiller du */}
      <section className="mt-20">
        <SectionKicker n={1} label="Sådan spiller du" icon={ScrollText} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight max-w-3xl">Sådan spiller du Festival Manager 2027</h2>
        <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          Festival Manager går ud på at forudsige næste års festivalprogrammer, før de bliver annonceret. Du vælger dine
          artister, og du får point, når dine gæt viser sig at være rigtige. Du konkurrerer både mod dine venner og resten af spillerne.
        </p>
      </section>

      {/* 02 – Festivalerne */}
      <section className="mt-24">
        <SectionKicker n={2} label="Festivalerne" icon={Flag} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Festivalerne</h2>
        <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          Du skal lave et program til fem festivaler: Roskilde Festival, NorthSide, Syd for Solen, Tinderbox og Smukfest.
        </p>

        <div className="mt-10 space-y-4 text-zinc-300 leading-relaxed max-w-3xl">
          <p>På Roskilde skal du vælge 4 headlinere, 16 internationale artister og 6 danske artister. Derudover skal du gætte, hvem der åbner Orange Scene.</p>
          <p>På NorthSide skal du vælge 3 headlinere, 5 internationale artister og 5 danske artister.</p>
          <p>På Syd for Solen skal du vælge 3 headlinere, 10 internationale artister og 4 danske artister.</p>
          <p>På Tinderbox skal du vælge 3 headlinere, 8 internationale artister og 6 danske artister.</p>
          <p>På Smukfest skal du vælge 4 headlinere, 7 internationale artister og 7 danske artister.</p>
          <p>Det giver 91 almindelige artistgæt i alt samt dit separate gæt på Roskildes Orange Scene-åbner. I alt 92 gæt.</p>
          <p>Hvis du rammer alle dine gæt i den kategori, du har placeret dem i, kan du maksimalt opnå 426 point.</p>
        </div>

        {/* Visual companion – festival slot table */}
        <div className="mt-10 card-flat overflow-hidden" data-testid="rules-festival-table">
          <div className="grid grid-cols-[1.4fr_repeat(4,minmax(0,1fr))] text-xs text-zinc-500 uppercase tracking-[0.18em] px-5 py-3 border-b border-white/5">
            <div>Festival</div>
            <div className="text-center">Headliner</div>
            <div className="text-center">Intl.</div>
            <div className="text-center">Dansk</div>
            <div className="text-center">Bonus</div>
          </div>
          {FESTIVAL_ROWS.map(f => (
            <div key={f.name} className="grid grid-cols-[1.4fr_repeat(4,minmax(0,1fr))] items-center px-5 py-4 border-b border-white/5 last:border-b-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-6 w-1 rounded-full flex-shrink-0" style={{ background: f.color }} />
                <span className="font-semibold truncate">{f.name}</span>
              </div>
              <div className="text-center font-score text-2xl">{f.head}</div>
              <div className="text-center font-score text-2xl">{f.intl}</div>
              <div className="text-center font-score text-2xl">{f.dansk}</div>
              <div className="text-center text-xs text-zinc-400">{f.extra || "—"}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 03 – Point */}
      <section className="mt-24">
        <SectionKicker n={3} label="Point" icon={Trophy} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Point</h2>
        <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          En headliner giver 12 point, en international artist giver 4 point, og en dansk artist giver 1 point. Hvis du rammer den artist, der åbner Orange Scene på Roskilde, får du 10 point.
        </p>

        {/* Point cards */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="rules-point-cards">
          {POINTS.map(p => (
            <div key={p.label} className="card-flat p-6 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full opacity-10" style={{ background: p.tone }} />
              <div className="label-eyebrow" style={{ color: p.tone }}>{p.label}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-score text-6xl leading-none">{p.value}</span>
                <span className="text-zinc-500 text-sm">pt</span>
              </div>
              <p className="mt-3 text-xs text-zinc-400 leading-relaxed">{p.hint}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-5 text-zinc-300 leading-relaxed max-w-3xl">
          <p>
            Et headliner-gæt giver kun 12 point, hvis artisten faktisk bliver annonceret som headliner af festivalen. Hvis du eksempelvis vælger Olivia Dean som headliner på Roskilde, men hun ender med at spille festivalen som international artist uden headliner-status, får du 4 point. Hvis du vælger en dansk artist som headliner, og artisten spiller festivalen uden at være headliner, får du 1 point. Hvis artisten ikke spiller på festivalen, får du 0 point.
          </p>
          <p>
            Headliner-status afgøres ud fra festivalens officielle kommunikation og fastlægges endeligt, når det er muligt at afgøre, hvem der officielt er headliner.
          </p>
        </div>
      </section>

      {/* 04 – Den samme artist */}
      <section className="mt-24 grid lg:grid-cols-[1fr_auto] gap-10 items-start">
        <div>
          <SectionKicker n={4} label="Den samme artist" icon={Users} />
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Den samme artist</h2>
          <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
            Du må gerne vælge den samme artist på flere festivaler, men den samme artist må maksimalt vælges på tre forskellige festivaler. Du kan ikke vælge den samme artist flere gange på den samme festival.
          </p>
        </div>
        <div className="card-flat p-6 min-w-[200px]">
          <div className="label-eyebrow">Maks. festivaler</div>
          <div className="font-score text-7xl leading-none mt-2">3</div>
          <div className="text-xs text-zinc-500 mt-2">pr. artist</div>
        </div>
      </section>

      {/* 05 – Deadline */}
      <section className="mt-24">
        <SectionKicker n={5} label="Deadline" icon={Clock} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Deadline</h2>
        <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          Du kan ændre dine gæt frit frem til deadline. Når deadline er nået, låses alle line-ups, og du kan ikke længere ændre, tilføje eller fjerne gæt. Herefter begynder festivalerne at offentliggøre deres programmer, og dine point bliver løbende opdateret.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="rules-deadline-timeline">
          <div className="card-flat p-5">
            <div className="flex items-center gap-2 text-emerald-400 label-eyebrow" style={{ color: "#34D399" }}>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Nu · Åbent
            </div>
            <div className="mt-2 font-semibold">Sæt dine gæt</div>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">Vælg, byt og ombestem dig frit.</p>
          </div>
          <div className="card-flat p-5">
            <div className="flex items-center gap-2 label-eyebrow" style={{ color: "#FB923C" }}>
              <Lock size={10} /> Ved deadline · Lås
            </div>
            <div className="mt-2 font-semibold">Line-ups fastlåses</div>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">Ingen ændringer, tilføjelser eller fjernelser.</p>
          </div>
          <div className="card-flat p-5">
            <div className="flex items-center gap-2 label-eyebrow" style={{ color: "#60A5FA" }}>
              <Sparkles size={10} /> Efter deadline · Scoring
            </div>
            <div className="mt-2 font-semibold">Point falder løbende</div>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">Festivalerne annoncerer – du får point.</p>
          </div>
        </div>
      </section>

      {/* 06 – Ligaer */}
      <section className="mt-24">
        <SectionKicker n={6} label="Ligaer" icon={Trophy} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Ligaer</h2>
        <div className="mt-5 space-y-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          <p>
            Der findes en samlet Festival Manager-liga og en separat liga for hver af de fem festivaler. Din placering opdateres løbende, efterhånden som nye navne bliver annonceret og point registreret.
          </p>
          <p>
            Du kan også oprette private ligaer og invitere dine venner, kolleger eller andre spillere. Private ligaer har deres egen rangliste, men følger de samme pointregler som resten af spillet. En spiller kan være medlem af flere private ligaer.
          </p>
        </div>
      </section>

      {/* 07 – Statistik */}
      <section className="mt-24">
        <SectionKicker n={7} label="Statistik" icon={Sparkles} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Statistik</h2>
        <div className="mt-5 space-y-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          <p>
            Indtil deadline er alle spilleres gæt private. Efter deadline åbnes statistikfunktionen for alle spillere. Her kan du blandt andet se, hvilke artister der er de mest populære gæt, hvilke headlinere der er mest populære, hvilke danske og internationale artister der er valgt oftest, hvilke gæt der er mest sjældne, og hvor stor en procentdel af spillerne der har valgt en bestemt artist.
          </p>
        </div>
      </section>

      {/* 08 – Fair play */}
      <section className="mt-24">
        <SectionKicker n={8} label="Fair play" icon={Lock} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Fair play</h2>
        <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          Ingen spillere kan se andres individuelle line-ups før deadline. Efter deadline bliver alle line-ups synlige, og spillerne kan sammenligne deres forudsigelser med hinanden.
        </p>
      </section>

      {/* 09 – Når sæsonen slutter */}
      <section className="mt-24">
        <SectionKicker n={9} label="Når sæsonen slutter" icon={Crown} />
        <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Når sæsonen slutter</h2>
        <div className="mt-5 space-y-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          <p>
            Pointene bliver registreret løbende, efterhånden som festivalerne annoncerer deres programmer. Når alle fem festivalers programmer er færdigbehandlet, afsluttes sæsonen. Herefter kåres vinderne af de enkelte festival-ligaer samt den samlede Festival Manager-liga.
          </p>
          <p>
            Den samlede vinder er den spiller, der har flest point på tværs af de fem festivaler.
          </p>
        </div>
      </section>

      {/* 10 – Badges */}
      <section className="mt-28" data-testid="rules-badges">
        <SectionKicker n={10} label="Badges" icon={Award} />
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Skjulte badges du kan låse op</h2>
          <div className="text-sm text-zinc-500">{BADGES.length} i alt</div>
        </div>
        <p className="mt-5 text-lg text-zinc-300 leading-relaxed max-w-3xl">
          Undervejs kan du låse skjulte badges op, når du rammer særlige milepæle. Badges giver ingen point og påvirker ikke ranglisten – de er et hædersmærke, der vises på din offentlige profil.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          {BADGES.map(b => (
            <div
              key={b.id}
              data-testid={`rules-badge-${b.id}`}
              className="card-flat p-5 flex items-start gap-4 relative overflow-hidden group"
            >
              <div
                className="absolute -left-8 -bottom-10 h-32 w-32 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
                style={{ background: b.color }}
              />
              <div
                className="h-14 w-14 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0 border"
                style={{ background: `${b.color}20`, borderColor: `${b.color}40` }}
                aria-hidden="true"
              >
                {b.icon}
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-lg" style={{ color: b.color }}>{b.name}</div>
                <p className="mt-1 text-sm text-zinc-300 leading-relaxed">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-zinc-500">
          Bemærk: andres låste badges er skjult, indtil de selv har låst dem op.
        </p>
      </section>
    </div>
  );
}
